import type { AssetManifest, PetPaletteResponse, PetStateWire } from '@kenocyber/shared';
import { findChassisFinish, findEnergyColor } from '@kenocyber/shared';
import { useState } from 'react';
import { staticAssetUrl } from '../game/assets';
import { bpsLabel, PetCopyPicker, shortId } from './PetCopyPicker';
import { PetCustomisation } from './PetCustomisation';
import { PetPreview3D } from './PetPreview3D';
import { PetStars } from './PetStars';
import { rarityLabel, rarityPaint, rarityStyle } from './rarity';

/**
 * Everything about ONE pet, in four sections instead of one wall.
 *
 * The panel this replaces put the copy list, the training rows, fifteen
 * finishes, ten energy colours, an evolution planner and every button on a
 * single scrolling column. At 1366x768 the result was that the two things a
 * player most needs - which copy am I acting on, and how do I evolve - were
 * below the fold, and the evolve button did not exist at all until a donor had
 * been chosen from a dropdown nobody scrolled to.
 *
 * So: GERAL says what this pet is, EVOLUIR does one job and states its result
 * before it happens, TREINO feeds, APARÊNCIA paints. The primary actions live
 * in a fixed footer and are therefore never scrolled away from.
 */

type PetSection = 'geral' | 'evoluir' | 'treino' | 'aparencia';

const SECTION_LABEL: Readonly<Record<PetSection, string>> = {
  geral: 'GERAL',
  evoluir: 'EVOLUIR',
  treino: 'TREINO',
  aparencia: 'APARÊNCIA',
};

interface PetDetailsProps {
  readonly pet: PetStateWire | null;
  readonly manifest: AssetManifest;
  readonly busy: boolean;
  readonly error: string | null;
  readonly palette: PetPaletteResponse | null;
  readonly selectedCopyId: string | null;
  readonly onSelectCopy: (instanceId: string) => void;
  readonly secondaryCopyId: string | null;
  readonly onSelectSecondary: (instanceId: string) => void;
  readonly onActivate: (instanceId: string | null) => void;
  readonly onEvolve: (first: string, second: string) => void;
  readonly onFeed: (petInstanceId: string, foodDefinitionId: string) => void;
  readonly onUnlock: (unlockDefinitionId: string) => void;
  readonly onApply: (petInstanceId: string, energyId: string, finishId: string) => void;
}

/** The art of the evolution a card should show: the active one, else the best. */
export function displayEvolution(pet: PetStateWire): number {
  const active = pet.copies.find((copy) => copy.active);
  if (active) {
    return active.evolution;
  }
  return pet.copies.reduce((best, copy) => Math.max(best, copy.evolution), 1);
}

export function PetDetailsPanel({
  pet,
  manifest,
  busy,
  error,
  palette,
  selectedCopyId,
  onSelectCopy,
  secondaryCopyId,
  onSelectSecondary,
  onActivate,
  onEvolve,
  onFeed,
  onUnlock,
  onApply,
}: PetDetailsProps) {
  const [section, setSection] = useState<PetSection>('geral');
  const [confirming, setConfirming] = useState(false);
  /**
   * The appearance being PREVIEWED, which is not the appearance being worn.
   *
   * Null means "show what the pet actually wears". A selection here changes the
   * preview model and nothing else: the pet in the arena keeps its colours
   * until APLICAR is pressed and the server answers.
   */
  const [preview, setPreview] = useState<{ energyId: string; finishId: string } | null>(null);

  if (!pet) {
    return (
      <aside className="inv-details" data-testid="pet-details">
        <p className="inv-state">Selecione um pet.</p>
      </aside>
    );
  }

  const owned = pet.copies.length > 0;
  const evolution = displayEvolution(pet);
  const form = pet.forms.find((entry) => entry.evolution === evolution) ?? pet.forms[0];
  const nextForm = pet.forms.find((entry) => entry.evolution === evolution + 1) ?? null;
  const url = form ? staticAssetUrl(manifest, form.assetKey) : null;
  const activeCopy = pet.copies.find((copy) => copy.active) ?? null;
  const bestEvolution = pet.copies.reduce((best, copy) => Math.max(best, copy.evolution), 0);

  /**
   * The copy every action applies to.
   *
   * Resolved from the player's EXPLICIT selection, never guessed. If the
   * selected id is not among the copies - it evolved away, or was retired -
   * this is null and every action is disabled until a new one is chosen,
   * because silently redirecting a food to a different pet is worse than
   * asking again.
   */
  const subject = pet.copies.find((copy) => copy.instanceId === selectedCopyId) ?? null;

  /**
   * The copies that could be consumed alongside the selected one.
   *
   * Same evolution, below the cap, not reserved, and never the primary itself.
   * Copies at a DIFFERENT evolution are absent rather than disabled: an
   * evolution never mixes forms, so offering one would be offering a mistake.
   */
  const donors = subject
    ? pet.copies.filter(
        (copy) =>
          copy.instanceId !== subject.instanceId &&
          copy.evolution === subject.evolution &&
          copy.evolution < pet.maxEvolution &&
          !copy.reserved,
      )
    : [];
  const secondary = donors.find((copy) => copy.instanceId === secondaryCopyId) ?? null;
  const canEvolve = subject !== null && secondary !== null && !subject.reserved;

  /*
   * The result, computed here and stated before anything is spent.
   *
   * Training sums exactly. Appearance follows the ACTIVE copy when either input
   * is active, and otherwise the primary - the same rule the server applies, so
   * the preview cannot promise something the answer will contradict.
   */
  const heir =
    subject && secondary ? (secondary.active && !subject.active ? secondary : subject) : null;
  const resultDamageBps =
    subject && secondary ? subject.training.damageBonusBps + secondary.training.damageBonusBps : 0;
  const resultSpeedBps =
    subject && secondary
      ? subject.training.attackSpeedBonusBps + secondary.training.attackSpeedBonusBps
      : 0;
  const staysActive = Boolean(subject?.active || secondary?.active);

  /** What the preview model wears: the pending selection, else the real one. */
  const previewEnergy = preview?.energyId ?? subject?.energyId ?? '';
  const previewFinish = preview?.finishId ?? subject?.finishId ?? '';
  const previewing = preview !== null;

  return (
    <aside
      className="inv-details pet-details"
      data-testid="pet-details"
      data-definition-id={pet.definitionId}
      data-section={section}
      style={rarityStyle(pet.rarity)}
    >
      <header className="inv-details-head">
        <div
          className="inv-details-art"
          data-category="pet"
          data-rarity={pet.rarity}
          data-paint={rarityPaint(pet.rarity)}
        >
          {url ? <img src={url} alt="" draggable={false} /> : <span aria-hidden="true">?</span>}
        </div>
        <div className="inv-details-title">
          <h3 data-testid="pet-detail-name">{pet.name}</h3>
          <span className="inv-details-sub" data-testid="pet-detail-rarity">
            {rarityLabel(pet.rarity)}
          </span>
          <PetStars stars={pet.stars} slots={pet.starSlots} testId="detail" inline />
          {/* The three facts a player asks first, on one line. */}
          <p className="pet-summary" data-testid="pet-summary">
            <span data-testid="pet-summary-copies">{pet.copies.length} cópias</span>
            {owned ? <span data-testid="pet-summary-best">máx. EV.{bestEvolution}</span> : null}
            {activeCopy ? (
              <span className="pet-summary-active" data-testid="pet-summary-active">
                ATIVO
              </span>
            ) : (
              <span data-testid="pet-summary-inactive">nenhuma ativa</span>
            )}
          </p>
        </div>
      </header>

      {/* One job per section, so no screen is a wall of controls. */}
      <nav className="pet-sections" data-testid="pet-sections">
        {(Object.keys(SECTION_LABEL) as PetSection[]).map((key) => (
          <button
            key={key}
            type="button"
            className="pet-section-tab"
            data-testid={`pet-section-${key}`}
            data-selected={section === key ? 'true' : 'false'}
            aria-pressed={section === key}
            onClick={() => setSection(key)}
          >
            {SECTION_LABEL[key]}
          </button>
        ))}
      </nav>

      <div className="inv-details-body">
        {/* The copy picker is above every section: which copy an action applies
            to is never a per-section question. */}
        <PetCopyPicker
          copies={pet.copies}
          selectedId={selectedCopyId}
          onSelect={onSelectCopy}
          disabled={busy}
          /* The still of each copy's own form, from the manifest this panel
             already holds. No third renderer, no canvas snapshot, and no
             request the screen was not going to make: the same three files
             back the pet cards and the detail header. */
          artUrl={(target) => {
            const entry = pet.forms.find((candidate) => candidate.evolution === target);
            return entry ? staticAssetUrl(manifest, entry.assetKey) : null;
          }}
        />
        {!subject && owned ? (
          <p className="inv-state" data-testid="pet-no-selection">
            Selecione uma cópia acima para agir sobre ela.
          </p>
        ) : null}

        {section === 'geral' ? (
          <div data-testid="pet-general">
            <div className="inv-detail-row">
              <span className="inv-detail-label">Evolução</span>
              <span className="inv-detail-value" data-testid="pet-detail-evolution">
                {owned ? `${form?.name ?? '—'} (${evolution} / ${pet.maxEvolution})` : '—'}
              </span>
            </div>
            <div className="inv-detail-row">
              <span className="inv-detail-label">Cópias</span>
              <span className="inv-detail-value" data-testid="pet-detail-copies">
                {pet.copies.length}
              </span>
            </div>
            <div className="inv-detail-row">
              <span className="inv-detail-label">Projéteis por salva</span>
              <span className="inv-detail-value" data-testid="pet-detail-muzzles">
                {form?.projectiles ?? 0}
              </span>
            </div>
            <p className="art-passive-note" data-testid="pet-detail-description">
              {owned
                ? form?.description
                : 'Ainda não obtido. Um pet luta ao lado da nave e nunca recebe dano.'}
            </p>
            {nextForm ? (
              <div className="art-effects" data-testid="pet-next-form">
                <h4>Próxima evolução</h4>
                <div className="inv-detail-row">
                  <span className="inv-detail-label">{nextForm.name}</span>
                  <span className="inv-detail-value">{nextForm.projectiles} projéteis</span>
                </div>
                <p className="art-combine-warning">{nextForm.description}</p>
              </div>
            ) : owned ? (
              <p className="pet-max" data-testid="pet-max-evolution">
                EVOLUÇÃO MÁXIMA
              </p>
            ) : null}
          </div>
        ) : null}

        {section === 'evoluir' ? (
          <div data-testid="pet-evolve-section">
            <div className="inv-detail-row">
              <span className="inv-detail-label">Evoluções possíveis</span>
              <span className="inv-detail-value" data-testid="pet-evolvable-pairs">
                {pet.evolvablePairs}
              </span>
            </div>

            {!subject ? null : subject.evolution >= pet.maxEvolution ? (
              <p className="pet-max" data-testid="pet-evolve-max">
                EVOLUÇÃO MÁXIMA — esta cópia não evolui mais.
              </p>
            ) : donors.length === 0 ? (
              <p className="inv-state" data-testid="pet-evolve-nodonor">
                Nenhuma outra cópia EV.{subject.evolution} disponível. São necessárias duas cópias
                da mesma evolução.
              </p>
            ) : (
              <>
                <div className="pet-evolve-pair">
                  <div className="pet-evolve-slot" data-testid="pet-evolve-primary">
                    <span className="inv-detail-label">PET PRINCIPAL</span>
                    <strong>
                      EV.{subject.evolution} · {shortId(subject.instanceId)}
                    </strong>
                    <span>{subject.active ? 'ATIVO' : 'inativo'}</span>
                  </div>
                  <span className="pet-evolve-plus" aria-hidden="true">
                    +
                  </span>
                  <div className="pet-evolve-slot" data-testid="pet-evolve-donor">
                    <span className="inv-detail-label">PET CONSUMIDO</span>
                    {secondary ? (
                      <>
                        <strong>
                          EV.{secondary.evolution} · {shortId(secondary.instanceId)}
                        </strong>
                        <span>{secondary.active ? 'ATIVO' : 'inativo'}</span>
                      </>
                    ) : (
                      <em>escolha abaixo</em>
                    )}
                  </div>
                </div>

                {/* The donor is CHOSEN, never inferred: it is the copy that
                    ceases to exist, and picking it for the player is not a
                    decision this panel gets to make. */}
                <label className="pet-evolve-secondary">
                  <span className="inv-detail-label">Consumir junto</span>
                  <select
                    data-testid="pet-evolve-secondary"
                    value={secondary?.instanceId ?? ''}
                    disabled={busy}
                    onChange={(event) => onSelectSecondary(event.target.value)}
                  >
                    <option value="">Selecione a segunda cópia…</option>
                    {donors.map((copy) => (
                      <option key={copy.instanceId} value={copy.instanceId}>
                        EV.{copy.evolution} · {shortId(copy.instanceId)}
                        {copy.active ? ' · ATIVO' : ''}
                      </option>
                    ))}
                  </select>
                </label>

                {secondary ? (
                  <div className="pet-evolve-outcome" data-testid="pet-evolve-outcome">
                    <h4>RESULTADO</h4>
                    <p className="pet-evolve-headline" data-testid="pet-evolve-result">
                      EV.{subject.evolution + 1} · DANO {bpsLabel(resultDamageBps)} · ASPD{' '}
                      {bpsLabel(resultSpeedBps)} ·{' '}
                      {findEnergyColor(heir?.energyId ?? '')?.name ?? '—'} ·{' '}
                      {findChassisFinish(heir?.finishId ?? '')?.name ?? '—'}
                      {staysActive ? ' · CONTINUA ATIVO' : ''}
                    </p>
                    <div className="inv-detail-row">
                      <span className="inv-detail-label">Treinamento somado</span>
                      <span className="inv-detail-value" data-testid="pet-evolve-training">
                        DANO {bpsLabel(resultDamageBps)} · ASPD {bpsLabel(resultSpeedBps)}
                      </span>
                    </div>
                    <div className="inv-detail-row">
                      <span className="inv-detail-label">Aparência herdada</span>
                      <span className="inv-detail-value" data-testid="pet-evolve-appearance">
                        {findEnergyColor(heir?.energyId ?? '')?.name ?? '—'} ·{' '}
                        {findChassisFinish(heir?.finishId ?? '')?.name ?? '—'}
                      </span>
                    </div>
                    <div className="inv-detail-row">
                      <span className="inv-detail-label">Continua ativo</span>
                      <span className="inv-detail-value" data-testid="pet-evolve-stays-active">
                        {staysActive ? 'SIM' : 'não'}
                      </span>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        {section === 'treino' && subject ? (
          <PetCustomisation
            pet={pet}
            copyInstanceId={subject.instanceId}
            training={subject.training}
            energyId={subject.energyId}
            finishId={subject.finishId}
            palette={palette}
            busy={busy}
            show="training"
            onFeed={(foodId) => onFeed(subject.instanceId, foodId)}
            onUnlock={onUnlock}
            onApply={(energyId, finishId) => onApply(subject.instanceId, energyId, finishId)}
          />
        ) : null}

        {section === 'aparencia' && subject ? (
          <>
            {/* The preview is a SEPARATE model with its own materials. Nothing
                selected here reaches the pet in the arena until APLICAR. */}
            <PetPreview3D
              definitionId={pet.definitionId}
              evolution={subject.evolution}
              energyId={previewEnergy}
              finishId={previewFinish}
              previewing={previewing}
              onReset={() => setPreview(null)}
            />
            <PetCustomisation
              pet={pet}
              copyInstanceId={subject.instanceId}
              training={subject.training}
              energyId={subject.energyId}
              finishId={subject.finishId}
              palette={palette}
              busy={busy}
              show="appearance"
              onFeed={(foodId) => onFeed(subject.instanceId, foodId)}
              onUnlock={onUnlock}
              onApply={(energyId, finishId) => {
                setPreview(null);
                onApply(subject.instanceId, energyId, finishId);
              }}
              /* Merged over the CURRENT preview, not over the worn pair: a
                 player choosing a finish and then a colour is composing one
                 look, not asking for the finish to be forgotten. */
              onPreview={(kind, id) =>
                setPreview((current) => ({
                  energyId: kind === 'energy' ? id : (current?.energyId ?? previewEnergy),
                  finishId: kind === 'finish' ? id : (current?.finishId ?? previewFinish),
                }))
              }
            />
          </>
        ) : null}
      </div>

      {/* Fixed action footer: an action a player has to scroll to find is not
          available to them. */}
      <footer className="inv-details-actions" data-testid="pet-actions">
        {error ? (
          <p className="inv-detail-error" role="alert" data-testid="pet-error">
            {error}
          </p>
        ) : null}

        <div className="pet-buttons">
          {activeCopy ? (
            <button
              type="button"
              className="inv-equip"
              data-testid="pet-deactivate"
              disabled={busy}
              onClick={() => onActivate(null)}
            >
              {busy ? '…' : 'DESATIVAR'}
            </button>
          ) : subject ? (
            <button
              type="button"
              className="inv-equip"
              data-testid="pet-activate"
              // The SELECTED copy, never the strongest: which one flies is the
              // player's choice, and they have just made it above.
              disabled={busy || subject.reserved}
              onClick={() => onActivate(subject.instanceId)}
            >
              {busy ? '…' : 'ATIVAR'}
            </button>
          ) : null}

          {canEvolve && !confirming && subject ? (
            <button
              type="button"
              className="inv-equip pet-evolve-primary-action"
              data-testid="pet-evolve"
              disabled={busy}
              onClick={() => setConfirming(true)}
            >
              {busy ? '…' : `EVOLUIR PARA EV.${subject.evolution + 1}`}
            </button>
          ) : null}
        </div>

        {canEvolve && subject && secondary && confirming ? (
          <div className="inv-confirm" data-testid="pet-evolve-confirm">
            <p data-testid="pet-evolve-confirm-result">
              RESULTADO: EV.{subject.evolution + 1} · DANO {bpsLabel(resultDamageBps)} · ASPD{' '}
              {bpsLabel(resultSpeedBps)} · {findEnergyColor(heir?.energyId ?? '')?.name ?? '—'} ·{' '}
              {findChassisFinish(heir?.finishId ?? '')?.name ?? '—'}
            </p>
            <p className="art-combine-warning" data-testid="pet-evolve-warning">
              Esta evolução consumirá permanentemente as duas cópias selecionadas.
            </p>
            <div className="inv-confirm-actions">
              <button
                type="button"
                data-testid="pet-evolve-confirm-yes"
                // Disabled the instant it is pressed: a double click cannot
                // become two requests, and the server would replay anyway.
                disabled={busy}
                onClick={() => {
                  setConfirming(false);
                  // Primary first: the server inherits appearance from it when
                  // neither copy is active.
                  onEvolve(subject.instanceId, secondary.instanceId);
                }}
              >
                {busy ? 'Evoluindo…' : 'Confirmar'}
              </button>
              <button
                type="button"
                data-testid="pet-evolve-cancel"
                onClick={() => setConfirming(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : null}
      </footer>
    </aside>
  );
}
